import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

declare global {
  interface Window {
    WOW: any;
  }
}

interface ContactFormData {
  fullName: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
}

const Contact: React.FC = () => {
  const [formData, setFormData] = useState<ContactFormData>({
    fullName: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    // Initialize data-background attributes
    const elements = document.querySelectorAll('[data-background]');
    elements.forEach((element) => {
      const bg = element.getAttribute('data-background');
      if (bg) {
        (element as HTMLElement).style.backgroundImage = `url(${bg})`;
      }
    });

    // Initialize WOW.js for scroll animations
    if (window.WOW) {
      const wow = new window.WOW({
        boxClass: 'wow',
        animateClass: 'animated',
        offset: 0,
        mobile: true,
        live: true
      });
      wow.init();
    }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const response = await fetch('http://localhost:5000/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSuccessMessage('Thank you for contacting us! We will get back to you soon.');
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });
        
        // Auto-hide success message after 5 seconds
        setTimeout(() => {
          setSuccessMessage('');
        }, 5000);
      } else {
        setErrorMessage('Failed to send message. Please try again.');
      }
    } catch (error) {
      setErrorMessage('An error occurred. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Page Banner */}
      <section 
        className="banner__inner-page bg-image pt-160 pb-160" 
        data-background="/assets/images/banner/banner-inner-page.jpg"
      >
        <div className="container">
          <h2 className="wow fadeInUp" data-wow-delay="00ms" data-wow-duration="1500ms">Contact Us</h2>
          <div className="breadcrumb-list wow fadeInUp" data-wow-delay="200ms" data-wow-duration="1500ms">
            <Link to="/">Home</Link>
            <span><i className="fa-regular fa-angles-right mx-2"></i>Contact</span>
          </div>
        </div>
      </section>

      {/* Contact Info Section */}
      <section className="contact-area pt-120 pb-60">
        <div className="container">
          <div className="row g-4">
            <div className="col-lg-4 col-md-6 wow fadeInUp" data-wow-delay="00ms" data-wow-duration="1500ms">
              <div 
                style={{
                  background: '#FFF5F2',
                  padding: '40px 30px',
                  borderRadius: '10px',
                  textAlign: 'center',
                  transition: 'all 0.3s ease',
                  border: '2px solid #FFE5DC',
                  height: '100%'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-10px)';
                  e.currentTarget.style.boxShadow = '0 10px 30px rgba(247, 79, 34, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{
                  width: '80px',
                  height: '80px',
                  background: '#03A84A',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 25px'
                }}>
                  <i className="fa-solid fa-location-dot" style={{ fontSize: '32px', color: 'white' }}></i>
                </div>
                <h4 style={{ color: '#0C2239', fontSize: '22px', fontWeight: '700', marginBottom: '15px' }}>Our Address</h4>
                <p style={{ color: '#666', fontSize: '16px', lineHeight: '1.8', margin: 0 }}>
                  Gokarneshowr -5, Jorpati Kathmandu, Nepali
                </p>
              </div>
            </div>
            <div className="col-lg-4 col-md-6 wow fadeInUp" data-wow-delay="200ms" data-wow-duration="1500ms">
              <div 
                style={{
                  background: '#FFF5F2',
                  padding: '40px 30px',
                  borderRadius: '10px',
                  textAlign: 'center',
                  transition: 'all 0.3s ease',
                  border: '2px solid #FFE5DC',
                  height: '100%'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-10px)';
                  e.currentTarget.style.boxShadow = '0 10px 30px rgba(247, 79, 34, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{
                  width: '80px',
                  height: '80px',
                  background: '#03A84A',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 25px'
                }}>
                  <i className="fa-solid fa-phone" style={{ fontSize: '32px', color: 'white' }}></i>
                </div>
                <h4 style={{ color: '#0C2239', fontSize: '22px', fontWeight: '700', marginBottom: '15px' }}>Phone Number</h4>
                <p style={{ color: '#666', fontSize: '16px', lineHeight: '1.8', margin: 0 }}>
                  <a href="tel:+977-9801031572" style={{ color: '#666', textDecoration: 'none' }}>+977-9801031572</a><br />
                  <a href="tel:+977-9851031573" style={{ color: '#666', textDecoration: 'none' }}>+977-9851031573</a>
                </p>
              </div>
            </div>
            <div className="col-lg-4 col-md-6 wow fadeInUp" data-wow-delay="400ms" data-wow-duration="1500ms">
              <div 
                style={{
                  background: '#FFF5F2',
                  padding: '40px 30px',
                  borderRadius: '10px',
                  textAlign: 'center',
                  transition: 'all 0.3s ease',
                  border: '2px solid #FFE5DC',
                  height: '100%'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-10px)';
                  e.currentTarget.style.boxShadow = '0 10px 30px rgba(247, 79, 34, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{
                  width: '80px',
                  height: '80px',
                  background: '#03A84A',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 25px'
                }}>
                  <i className="fa-solid fa-envelope" style={{ fontSize: '32px', color: 'white' }}></i>
                </div>
                <h4 style={{ color: '#0C2239', fontSize: '22px', fontWeight: '700', marginBottom: '15px' }}>Email Address</h4>
                <p style={{ color: '#666', fontSize: '16px', lineHeight: '1.8', margin: 0 }}>
                  <a href="mailto:newlifnepal071@gmail.com" style={{ color: '#666', textDecoration: 'none' }}>newlifnepal071@gmail.com</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form & Map Section */}
      <section className="pb-120">
        <div className="container">
          <div className="row g-5">
            <div className="col-lg-6">
              <div className="wow fadeInUp" data-wow-delay="00ms" data-wow-duration="1500ms" style={{ height: '100%', borderRadius: '10px', overflow: 'hidden' }}>
                <iframe 
                  src="https://maps.google.com/maps?q=New+Life+Nepal,+Budhanilkantha,+Kathmandu&t=&z=15&ie=UTF8&iwloc=&output=embed" 
                  width="100%" 
                  height="100%" 
                  style={{ border: 0, minHeight: '600px' }} 
                  allowFullScreen 
                  loading="lazy"
                  title="New Life Nepal Location"
                ></iframe>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="wow fadeInUp" data-wow-delay="200ms" data-wow-duration="1500ms" style={{
                background: '#FFF5F2',
                padding: '50px',
                borderRadius: '10px',
                border: '2px solid #FFE5DC'
              }}>
                <div style={{ marginBottom: '40px' }}>
                  <h5 style={{
                    color: '#03A84A',
                    fontSize: '18px',
                    fontWeight: '600',
                    marginBottom: '10px',
                    textAlign: 'center'
                  }}>
                    <i className="fa-regular fa-angles-left pe-1"></i> Get In Touch <i className="fa-regular fa-angles-right ps-1"></i>
                  </h5>
                  <h2 style={{
                    color: '#0C2239',
                    fontSize: '40px',
                    fontWeight: '700',
                    textAlign: 'center',
                    marginBottom: '10px'
                  }}>Send Us A Message</h2>
                  <p style={{ textAlign: 'center', color: '#666', fontSize: '16px' }}>We'd love to hear from you. Please fill out the form below.</p>
                </div>

                {successMessage && (
                  <div style={{
                    background: '#d4edda',
                    border: '1px solid #c3e6cb',
                    color: '#155724',
                    padding: '15px',
                    borderRadius: '5px',
                    marginBottom: '20px',
                    textAlign: 'center'
                  }}>
                    {successMessage}
                  </div>
                )}

                {errorMessage && (
                  <div style={{
                    background: '#f8d7da',
                    border: '1px solid #f5c6cb',
                    color: '#721c24',
                    padding: '15px',
                    borderRadius: '5px',
                    marginBottom: '20px',
                    textAlign: 'center'
                  }}>
                    {errorMessage}
                  </div>
                )}

                <form onSubmit={handleSubmit}>
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{
                      display: 'block',
                      color: '#0C2239',
                      fontSize: '16px',
                      fontWeight: '600',
                      marginBottom: '8px'
                    }}>Full Name *</label>
                    <input 
                      type="text" 
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      required
                      style={{
                        width: '100%',
                        padding: '15px',
                        border: '2px solid #FFE5DC',
                        borderRadius: '8px',
                        fontSize: '16px',
                        transition: 'all 0.3s ease',
                        background: 'white'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#03A84A'}
                      onBlur={(e) => e.target.style.borderColor = '#FFE5DC'}
                      placeholder="Enter your full name"
                    />
                  </div>

                  <div style={{ marginBottom: '20px' }}>
                    <label style={{
                      display: 'block',
                      color: '#0C2239',
                      fontSize: '16px',
                      fontWeight: '600',
                      marginBottom: '8px'
                    }}>Email Address *</label>
                    <input 
                      type="email" 
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      style={{
                        width: '100%',
                        padding: '15px',
                        border: '2px solid #FFE5DC',
                        borderRadius: '8px',
                        fontSize: '16px',
                        transition: 'all 0.3s ease',
                        background: 'white'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#03A84A'}
                      onBlur={(e) => e.target.style.borderColor = '#FFE5DC'}
                      placeholder="Enter your email address"
                    />
                  </div>

                  <div style={{ marginBottom: '20px' }}>
                    <label style={{
                      display: 'block',
                      color: '#0C2239',
                      fontSize: '16px',
                      fontWeight: '600',
                      marginBottom: '8px'
                    }}>Phone Number</label>
                    <input 
                      type="tel" 
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      style={{
                        width: '100%',
                        padding: '15px',
                        border: '2px solid #FFE5DC',
                        borderRadius: '8px',
                        fontSize: '16px',
                        transition: 'all 0.3s ease',
                        background: 'white'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#03A84A'}
                      onBlur={(e) => e.target.style.borderColor = '#FFE5DC'}
                      placeholder="Enter your phone number"
                    />
                  </div>

                  <div style={{ marginBottom: '20px' }}>
                    <label style={{
                      display: 'block',
                      color: '#0C2239',
                      fontSize: '16px',
                      fontWeight: '600',
                      marginBottom: '8px'
                    }}>Subject *</label>
                    <input 
                      type="text" 
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      style={{
                        width: '100%',
                        padding: '15px',
                        border: '2px solid #FFE5DC',
                        borderRadius: '8px',
                        fontSize: '16px',
                        transition: 'all 0.3s ease',
                        background: 'white'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#03A84A'}
                      onBlur={(e) => e.target.style.borderColor = '#FFE5DC'}
                      placeholder="What is this about?"
                    />
                  </div>

                  <div style={{ marginBottom: '30px' }}>
                    <label style={{
                      display: 'block',
                      color: '#0C2239',
                      fontSize: '16px',
                      fontWeight: '600',
                      marginBottom: '8px'
                    }}>Your Message *</label>
                    <textarea 
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      style={{
                        width: '100%',
                        padding: '15px',
                        border: '2px solid #FFE5DC',
                        borderRadius: '8px',
                        fontSize: '16px',
                        transition: 'all 0.3s ease',
                        background: 'white',
                        resize: 'vertical'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#03A84A'}
                      onBlur={(e) => e.target.style.borderColor = '#FFE5DC'}
                      placeholder="Write your message here..."
                    ></textarea>
                  </div>

                  <button 
                    type="submit" 
                    disabled={loading}
                    style={{
                      width: '100%',
                      padding: '18px',
                      background: loading ? '#ccc' : '#03A84A',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '18px',
                      fontWeight: '700',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      transition: 'all 0.3s ease',
                      textTransform: 'uppercase',
                      letterSpacing: '1px'
                    }}
                    onMouseEnter={(e) => !loading && (e.currentTarget.style.background = '#d63d0f')}
                    onMouseLeave={(e) => !loading && (e.currentTarget.style.background = '#03A84A')}
                  >
                    {loading ? 'Sending...' : 'Send Message'} <i className="fa-regular fa-paper-plane ms-2"></i>
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;
